import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from './api'
import type {
  ContactMessageCategory,
  ContactMessageDetailResponse,
  ContactMessageReply,
  ContactMessageStatus,
  UpdateContactMessagePayload,
} from './types'

const QK = {
  overview: ['platform', 'overview'] as const,
  tenants: (includeDeleted: boolean) => ['platform', 'tenants', includeDeleted] as const,
  customerUsers: (search: string) => ['platform', 'customer-users', search] as const,
  staffMembers: ['platform', 'staff', 'members'] as const,
  staffRoles: ['platform', 'staff', 'roles'] as const,
  staffPermissions: ['platform', 'staff', 'permissions'] as const,
  staffInvitations: ['platform', 'staff', 'invitations'] as const,
  audit: (action: string, resource: string, limit: number, offset: number) =>
    ['platform', 'audit', action, resource, limit, offset] as const,
  tables: ['platform', 'db-tools', 'tables'] as const,
  table: (name: string) => ['platform', 'db-tools', 'table', name] as const,
  contactMessages: (
    status: string,
    category: string,
    search: string,
    limit: number,
    offset: number,
  ) => ['platform', 'contact-messages', status, category, search, limit, offset] as const,
  contactMessage: (id: number) => ['platform', 'contact-message', id] as const,
}

export function useOverview() {
  return useQuery({ queryKey: QK.overview, queryFn: platformApi.overview })
}

export function useTenants(includeDeleted = false) {
  return useQuery({
    queryKey: QK.tenants(includeDeleted),
    queryFn: () => platformApi.listTenants(includeDeleted),
  })
}

export function useCreateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.createTenant,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  })
}

export function useChangePlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: string }) => platformApi.changePlan(id, plan),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  })
}

export function useSuspendTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.suspendTenant,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  })
}
export function useActivateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.activateTenant,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  })
}
export function useDeleteTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.deleteTenant,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  })
}
export function useImpersonate() {
  return useMutation({ mutationFn: platformApi.impersonate })
}

export function useCustomerUsers(search: string) {
  return useQuery({
    queryKey: QK.customerUsers(search),
    queryFn: () => platformApi.listCustomerUsers({ search, limit: 100 }),
  })
}

export function useDeactivateCustomerUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.deactivateUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'customer-users'] }),
  })
}
export function useActivateCustomerUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.activateUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'customer-users'] }),
  })
}
export function useDeleteCustomerUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.deleteUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'customer-users'] }),
  })
}

export function useStaffMembers() {
  return useQuery({ queryKey: QK.staffMembers, queryFn: platformApi.listStaffMembers })
}
export function useStaffRoles() {
  return useQuery({ queryKey: QK.staffRoles, queryFn: platformApi.listStaffRoles })
}
export function useStaffPermissions() {
  return useQuery({ queryKey: QK.staffPermissions, queryFn: platformApi.listStaffPermissions })
}
export function useStaffInvitations() {
  return useQuery({ queryKey: QK.staffInvitations, queryFn: platformApi.listStaffInvitations })
}

export function useChangeStaffRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: number; roleId: number }) =>
      platformApi.changeStaffRole(userId, roleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'staff'] }),
  })
}
export function useDeactivateStaffMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.deactivateStaffMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'staff'] }),
  })
}
export function useActivateStaffMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.activateStaffMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'staff'] }),
  })
}
export function useDeleteStaffMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.deleteStaffMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'staff'] }),
  })
}
export function useSetStaffRolePermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ roleId, permissions }: { roleId: number; permissions: string[] }) =>
      platformApi.setStaffRolePermissions(roleId, permissions),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'staff'] }),
  })
}
export function useCreateStaffInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, roleId }: { email: string; roleId: number }) =>
      platformApi.createStaffInvitation(email, roleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'staff', 'invitations'] }),
  })
}
export function useRevokeStaffInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.revokeStaffInvitation,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'staff', 'invitations'] }),
  })
}

export function useAuditLogs(params: {
  action: string
  resource_type: string
  limit?: number
  offset?: number
}) {
  const limit = params.limit ?? 50
  const offset = params.offset ?? 0
  return useQuery({
    queryKey: QK.audit(params.action, params.resource_type, limit, offset),
    queryFn: () =>
      platformApi.listAuditLogs({ ...params, limit, offset }),
    placeholderData: (prev) => prev,
  })
}

export function useDeleteAuditLogs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: Array<number | string>) => platformApi.deleteAuditLogs(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'audit'] }),
  })
}

export function useContactMessages(params: {
  status: ContactMessageStatus | ''
  category: ContactMessageCategory | ''
  search: string
  limit?: number
  offset?: number
}) {
  const limit = params.limit ?? 50
  const offset = params.offset ?? 0
  return useQuery({
    queryKey: QK.contactMessages(params.status, params.category, params.search, limit, offset),
    queryFn: () =>
      platformApi.listContactMessages({ ...params, limit, offset }),
    placeholderData: (prev) => prev,
    // Refresh while the staff member sits on the inbox so new
    // submissions and customer replies surface without manual refresh.
    refetchInterval: 30_000,
  })
}

export function useContactMessage(id: number | null) {
  return useQuery({
    queryKey: QK.contactMessage(id ?? 0),
    queryFn: () => platformApi.getContactMessage(id as number),
    enabled: !!id,
    // Detail modal - poll every 5s while open so a customer reply
    // shows up without the staff member reopening the row.
    refetchInterval: 5_000,
  })
}

export function useUpdateContactMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateContactMessagePayload }) =>
      platformApi.updateContactMessage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'contact-messages'] }),
  })
}

export function useDeleteContactMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.deleteContactMessage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'contact-messages'] }),
  })
}

export function useReplyContactMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      platformApi.replyContactMessage(id, body),
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: QK.contactMessage(id) })
      const previous = qc.getQueryData<ContactMessageDetailResponse>(QK.contactMessage(id))
      const tempReply: ContactMessageReply = {
        id: -Date.now(),
        contact_message_id: id,
        author_user_id: null,
        author_type: 'staff',
        author_name: null,
        author_email: null,
        body,
        created_at: new Date().toISOString(),
      }
      qc.setQueryData<ContactMessageDetailResponse>(QK.contactMessage(id), (old) =>
        old ? { ...old, replies: [...old.replies, tempReply] } : old,
      )
      return { previous }
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(QK.contactMessage(vars.id), ctx.previous)
    },
    onSettled: (_d, _err, vars) => {
      qc.invalidateQueries({ queryKey: QK.contactMessage(vars.id) })
      qc.invalidateQueries({ queryKey: ['platform', 'contact-messages'] })
    },
  })
}

export function useDbTables() {
  return useQuery({ queryKey: QK.tables, queryFn: platformApi.listTables })
}
export function useDbTable(name: string | null) {
  return useQuery({
    queryKey: QK.table(name ?? ''),
    queryFn: () => platformApi.fetchTable(name as string),
    enabled: !!name,
  })
}
export function useDeleteAllRows() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: platformApi.deleteAllRows,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'db-tools'] }),
  })
}
